import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useNavigate } from 'react-router-dom';
import HeruLogoIcon from '../../../assets/img/svg/heru-logo-v3.svg';
import ROUTES, { ROUTES_SLUGS } from '../../../shared/constants/routes';
import {
	Files,
	House,
	Receipt,
	ReadCvLogo,
	ChartLineUp,
	ChartPieSlice,
	UserCircle,
	SignOut,
	Headset,
	Folders,
	CalendarDots,
	Invoice,
} from '@phosphor-icons/react';
import SidebarItem, { SidebarItemType } from './SidebarItem';
import { removeToken } from '../../../redux/slices/auth.slice';
import { isSessionStorageEnabled, useStorage } from '../../../shared/utils/general.util';
import { logout } from '../../../redux/slices/auth.slice';
import LOCAL_STORAGE_KEY from '../../../shared/constants/localStorageKey';
import SESSION_STORAGE_KEY from '../../../shared/constants/sessionStorageKey';
import { restartAppState } from '../../../redux/slices/sat.slice';
import { restartTaxPayerData } from '../../../redux/slices/taxProfile.slice';
import { setOverwriteUtm, updateUTMs } from '../../../redux/slices/cart.slice';
import {
	ANALYTICS_EVENTS,
	EVENT_TYPES,
} from '../../../shared/constants/events';
import useCleverTapEvent from '../../../hooks/useClevertapEvent';
import RappiLogo from '../../../assets/img/svg/rappi-logo.svg';
import ZubaleLogo from '../../../assets/img/png/zubaleLogo.png';
import { useEffect, useState } from 'react';
import useAnnualLogic from '../../../hooks/useAnnualLogic';

const CollapsibleSidebar = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const logClevertapEvent = useCleverTapEvent();
	const { userHasPendingActions } = useAnnualLogic();
	const { tags } = useSelector((state: RootState) => state.profile);

	const [menuItems, setMenuItems] = useState<{
		top: SidebarItemType[];
		bottom: SidebarItemType[];
	}>({
		top: [
			{
				label: 'Inicio',
				icon: <House size={20} />,
				route: ROUTES.ROOT,
				slug: ROUTES_SLUGS.HOME,
			},
			{
				label: 'Declaración anual 2024',
				icon: <Folders />,
				route: ROUTES.ANNUAL_DECLARATION,
				slug: ROUTES_SLUGS.ANNUAL_DECLARATION,
			},
			{
				label: 'Planes de contabilidad',
				icon: <Files />,
				route: ROUTES.PLANS_AND_PRICES,
				slug: ROUTES_SLUGS.PLANS_AND_PRICES,
			},
			{
				label: 'Genera tus facturas',
				icon: <Receipt />,
				route: ROUTES.INVOICING,
				slug: ROUTES_SLUGS.INVOICING,
			},
			{
				label: 'Tus facturas en el SAT',
				icon: <ReadCvLogo />,
				route: ROUTES.FISCAL_DOCUMENTS,
				slug: ROUTES_SLUGS.FISCAL_DOCUMENTS,
			},
			{
				label: 'Calendario de declaraciones',
				icon: <ChartLineUp />,
				route: ROUTES.TAXES,
				slug: ROUTES_SLUGS.TAXES,
			},
		],
		bottom: [
			{
				label: 'Asistencia de soporte',
				icon: <Headset />,
				route: ROUTES.SUPPORT,
				slug: ROUTES_SLUGS.SUPPORT,
			},
			{
				label: 'Estado de tus compras',
				icon: <ChartPieSlice />,
				route: ROUTES.SERVICE_STATUS,
				slug: ROUTES_SLUGS.SERVICE_STATUS,
			},
			{
				label: 'Tu perfil heru',
				icon: <UserCircle />,
				route: ROUTES.USER_PROFILE,
				slug: ROUTES_SLUGS.USER_PROFILE,
			},
		],
	});

	const { sidebarIsExpanded: sidebarIsExpandedFromRedux } = useSelector(
		(state: RootState) => state.ui,
	);
	const rappiCertificates = useSelector(
		(state: RootState) => state.rappi.certificates,
	);
	const zubaleCertificates = useSelector(
		(state: RootState) => state.zubale.certificates,
	);

	const onLogout = () => {
		useStorage().removeItem(LOCAL_STORAGE_KEY.TOKEN);
		useStorage().removeItem(SESSION_STORAGE_KEY.SHOW_NO_INFO_MODAL);
		dispatch(removeToken());
		dispatch(restartAppState());
		dispatch(restartTaxPayerData());
		dispatch(logout());
		navigate(ROUTES.LOGIN);
	};

	const handleItemClick = ({
		slug,
		route,
	}: {
		slug: string;
		route: string;
	}) => {
		if (slug === 'plans') {
			if (isSessionStorageEnabled()) {
				const overwriteUtmKey = LOCAL_STORAGE_KEY.OVERWRITE_UTM;

				let overwriteUtm = sessionStorage.getItem(overwriteUtmKey);

				if (overwriteUtm === null) {
					sessionStorage.setItem(overwriteUtmKey, 'true');
					overwriteUtm = 'true';
				}
			} else {
				const currentOverwriteUtm = useSelector(
					(state: RootState) => state.cart.overwriteUtm,
				);

				if (currentOverwriteUtm === undefined || currentOverwriteUtm === null) {
					dispatch(setOverwriteUtm(true));
				}

				if (currentOverwriteUtm === true) {
				} else {
					const currentUTMs = useSelector(
						(state: RootState) => state.cart.utmInfo,
					);
					if (!currentUTMs) return;
					const updatedUTMs = {
						...currentUTMs,
						utm_campaign: currentUTMs?.utm_campaign,
						utm_medium: currentUTMs?.utm_medium,
						utm_source: currentUTMs?.utm_source,
					};
					dispatch(updateUTMs(updatedUTMs));
				}
			}
		}

		switch (slug) {
			case ROUTES_SLUGS.FISCAL_DOCUMENTS:
				logClevertapEvent(ANALYTICS_EVENTS.FISCAL_DOCUMENTS, {
					event_type: EVENT_TYPES.FISCAL_DOCUMENTS_VIEW_FISCAL_DOCUMENTS,
				});
				break;
			case ROUTES_SLUGS.RAPPI_CERTIFICATES:
				logClevertapEvent(ANALYTICS_EVENTS.RAPPI_CERTIFICATES, {
					event_type: EVENT_TYPES.RAPPI_CERTIFICATES_VIEW,
				});
				break;
			default:
				break;
		}

		navigate(route);
	};

	useEffect(() => {
		setMenuItems((prevMenuItems) => ({
			...prevMenuItems,
			top: prevMenuItems.top.map((item) =>
				item.slug === ROUTES_SLUGS.ANNUAL_DECLARATION
					? {
							...item,
							notificationIcon: userHasPendingActions ? (
								<div className='bg-red-600 size-2.5 border-2 border-neutral-300 rounded-full absolute -top-1 -left-2' />
							) : undefined,
						}
					: item,
			),
		}));
	}, [userHasPendingActions]);

	useEffect(() => {
		if (rappiCertificates?.length && !zubaleCertificates?.length) {
			const newTopItems = menuItems.top.filter(
				(item) => item.slug !== ROUTES_SLUGS.RAPPI_CERTIFICATES,
			);
			setMenuItems({
				top: [
					...newTopItems,
					{
						label: 'Certificados Rappi',
						icon: (
							<img
								src={RappiLogo}
								alt='Rappi'
								className='w-10 h-10'
							/>
						),
						route: ROUTES.RAPPI_CERTIFICATES,
						slug: ROUTES_SLUGS.RAPPI_CERTIFICATES,
					},
				],
				bottom: menuItems.bottom,
			});
		} else if (zubaleCertificates?.length && !rappiCertificates?.length) {
			const newTopItems = menuItems.top.filter(
				(item) => item.slug !== ROUTES_SLUGS.ZUBALE_CERTIFICATES,
			);
			setMenuItems({
				top: [
					...newTopItems,
					{
						label: 'Certificados Zúbale',
						icon: (
							<img
								src={ZubaleLogo}
								alt='Zubale'
								style={{ width: '22px', height: '22px' }}
							/>
						),
						route: ROUTES.ZUBALE_CERTIFICATES,
						slug: ROUTES_SLUGS.ZUBALE_CERTIFICATES,
					},
				],
				bottom: menuItems.bottom,
			});
		} else if (rappiCertificates?.length && zubaleCertificates?.length) {
			let newTopItems = menuItems.top.filter(
				(item) => item.slug !== ROUTES_SLUGS.GENERAL_CERTIFICATES,
			);
			newTopItems = newTopItems.filter(
				(item) => item.slug !== ROUTES_SLUGS.RAPPI_CERTIFICATES,
			);
			newTopItems = newTopItems.filter(
				(item) => item.slug !== ROUTES_SLUGS.ZUBALE_CERTIFICATES,
			);
			setMenuItems({
				top: [
					...newTopItems,
					{
						label: 'Certificados',
						icon: <Invoice size={20} />,
						route: ROUTES.GENERAL_CERTIFICATES,
						slug: ROUTES_SLUGS.GENERAL_CERTIFICATES,
					},
				],
				bottom: menuItems.bottom,
			});
		}
	}, [rappiCertificates, zubaleCertificates]);

	useEffect(() => {
		const showNewImssRegulations = tags?.some(
			(tag) => tag.tag === 'new-imss-regulations',
		);
		if (showNewImssRegulations) {
			setMenuItems((prevMenuItems) => {
				const hasImssRegulations = prevMenuItems.top.some(
					(item) => item.slug === ROUTES_SLUGS.NEW_IMS_REGULATIONS,
				);

				if (hasImssRegulations) {
					return prevMenuItems;
				}

				return {
					...prevMenuItems,
					top: [
						prevMenuItems.top[0],
						{
							label: 'Nueva regulación IMSS',
							icon: <CalendarDots />,
							route: ROUTES.NEW_IMS_REGULATIONS,
							slug: ROUTES_SLUGS.NEW_IMS_REGULATIONS,
						},
						...prevMenuItems.top.slice(1),
					],
				};
			});
		}
	}, [tags]);

	const [isHovering, setIsHovering] = useState(false);

	const sidebarIsExpanded = sidebarIsExpandedFromRedux || isHovering;

	return (
		<aside
			className={`h-screen hidden md:flex fixed top-0 z-[90] text-sm bg-white border-r border-h-neutral-90/70 transition-all p-3 w-full flex-col ${
				sidebarIsExpanded ? 'max-w-[280px]' : 'min-w-[80px] max-w-[80px]'
			}`}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			<div className='flex items-center justify-between'>
				<img
					src={HeruLogoIcon}
					alt='heru_logo'
					className={`h-10 w-[55px] cursor-pointer transition-all ${
						sidebarIsExpanded ? 'ml-4' : 'ml-0'
					}`}
					onClick={() => navigate(ROUTES.ROOT)}
				/>
			</div>
			<div className='flex-1 mt-6 flex flex-col gap-3 justify-between'>
				<div className='flex flex-col gap-1'>
					{menuItems.top.map((item, index) => {
						const isActive = location.pathname === item.route;

						return (
							<SidebarItem
								key={item.label}
								index={index}
								item={item}
								isActive={isActive}
								sidebarIsExpanded={sidebarIsExpanded}
								onClick={() => handleItemClick(item)}
							/>
						);
					})}
				</div>
				<div className='flex flex-col gap-3 pb-2'>
					<div className='flex flex-col gap-1'>
						{menuItems.bottom.map((item, index) => {
							const isActive = location.pathname === item.route;
							return (
								<SidebarItem
									key={item.label}
									index={index}
									item={item}
									isActive={isActive}
									sidebarIsExpanded={sidebarIsExpanded}
									onClick={() => navigate(item.route)}
								/>
							);
						})}
					</div>
					<hr />
					<SidebarItem
						index={menuItems.bottom.length}
						item={{
							label: 'Cerrar sesión',
							icon: <SignOut />,
							route: ROUTES.LOGIN,
							slug: ROUTES_SLUGS.LOGIN,
						}}
						isActive={false}
						sidebarIsExpanded={sidebarIsExpanded}
						onClick={onLogout}
					/>
				</div>
			</div>
		</aside>
	);
};

export default CollapsibleSidebar;
